import { AuthContext } from '@core/shared';
import { useSiteSettingsContext } from '@core/shared/src/components/SiteSettingsProvider';
import { WebinarContentSummary } from '@core/swagger-client';
import { WebinarStatus } from '@core/types';
import { Menu, MenuItem, Options, TestWebinarBadge, useTestWebinarDeleteOn, WarningOutlined } from '@core/ui';
import { Link } from '@online-webinar/ui-frontend';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ListWebinarCardDate,
    ListWebinarCardHeader,
    MenuItemOption,
    OnDemandStatusBadge,
    WebinarButton,
    WebinarDataFollowers,
    WebinarStatusMessage
} from '../../atoms';
import { WebinarActions } from '../WebinarActions';

export const ListWebinarCard = ({ webinar }: { webinar: WebinarContentSummary }) => {
    const { t } = useTranslation();
    const canManageEmailLists = useContext(AuthContext).canManageEmailLists;
    const isInactiveStatus = webinar.status === WebinarStatus.Cancelled || webinar.status === WebinarStatus.Expired;
    const isWhitelabel = useSiteSettingsContext().isWhitelabel;

    // Hooks run unconditionally; the value is only shown for a test webinar.
    const deleteOn = useTestWebinarDeleteOn(webinar.broadcastDateTime);

    return (
        <article
            data-webinar-id={webinar.webinarId}
            className={
                'webinar-item ' +
                (webinar.visibility && webinar.isPublished && !isInactiveStatus ? 'after:bg-primary' : '') +
                (!webinar.visibility && webinar.isPublished && !isInactiveStatus ? 'after:bg-secondary' : '') +
                (!webinar.isPublished && !isInactiveStatus ? 'after:bg-warning' : '') +
                (webinar.status === WebinarStatus.Started ? 'after:bg-success' : '') +
                (isInactiveStatus ? 'after:bg-gray-400' : '')
            }
        >
            {!webinar.isPublished && (
                <span>
                    <WarningOutlined />
                    {t('Webinar.Warning.NotPublished')}
                </span>
            )}
            <div>
                <ListWebinarCardHeader isInactive={isInactiveStatus} isVisible={webinar.visibility}>
                    {webinar.title}
                </ListWebinarCardHeader>
                {webinar.isTest && (
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        <TestWebinarBadge />
                        <span className="text-sm">{t('Webinar.Test.DeleteOn', { deleteOn })}</span>
                    </div>
                )}
                <ListWebinarCardDate
                    status={webinar.status}
                    broadcastDateTime={webinar.broadcastDateTime}
                    startedTime={webinar.startedOn ?? webinar.broadcastDateTime}
                    webinarDuration={webinar.status !== WebinarStatus.Ended ? webinar.plannedDuration : webinar.actualDuration || webinar.plannedDuration}
                    isCancelled={isInactiveStatus}
                />
                {webinar.status === WebinarStatus.Ended && (
                    <div className="mb-2">
                        <OnDemandStatusBadge status={webinar.onDemandStatus} />
                    </div>
                )}

                {((!webinar.hasBooking && webinar.status == WebinarStatus.Planned) || isInactiveStatus) && (
                    <WebinarStatusMessage hasBooking={webinar.hasBooking} status={webinar.status} />
                )}
                <div className="flex flex-col xs:flex-row gap-y-6">
                    <WebinarDataFollowers webinar={webinar} />
                    <WebinarActions>
                        <Menu
                            position="bottom"
                            classNames="btn btn--outline-primary btn--icon"
                            menuComponent={<Options classNames="size-6 2xl:size-7 2xl:m-0.5 fill-current" />}
                        >
                            <MenuItem>
                                <MenuItemOption to="/$webinarId/content" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'overview' }}>
                                    {t('OptionMenu.Content')}
                                </MenuItemOption>
                            </MenuItem>
                            <MenuItem>
                                <MenuItemOption to="/$webinarId/security" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'overview' }}>
                                    {t('OptionMenu.Security')}
                                </MenuItemOption>
                            </MenuItem>
                            <MenuItem>
                                <MenuItemOption to="/$webinarId/share" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'overview' }}>
                                    {webinar.status === WebinarStatus.Ended ? t('OptionMenu.ShareOnDemand') : t('OptionMenu.Share')}
                                </MenuItemOption>
                            </MenuItem>
                            {!!isWhitelabel && canManageEmailLists && (
                                <MenuItem>
                                    <MenuItemOption to="/$webinarId/marketing" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'overview' }}>
                                        {t('OptionMenu.Marketing')}
                                    </MenuItemOption>
                                </MenuItem>
                            )}
                            <MenuItem>
                                <MenuItemOption to="/$webinarId/insights" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'overview' }}>
                                    {t('OptionMenu.Insights')}
                                </MenuItemOption>
                            </MenuItem>
                            {webinar.numberOfSlides !== 0 && (webinar.status === WebinarStatus.Planned || webinar.status === WebinarStatus.Started) && (
                                <MenuItem>
                                    <MenuItemOption to="/$webinarId/slide-controller" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'overview' }}>
                                        {t('OptionMenu.SlideController')}
                                    </MenuItemOption>
                                </MenuItem>
                            )}
                            {(webinar.status === WebinarStatus.Planned || webinar.status === WebinarStatus.Started) && (
                                <MenuItem>
                                    <MenuItemOption to="/test-suite">{t('OptionMenu.Test')}</MenuItemOption>
                                </MenuItem>
                            )}
                            {webinar.status === WebinarStatus.Ended && (
                                <MenuItem>
                                    <MenuItemOption to="/$webinarId/edit-video" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'overview' }}>
                                        {t('OptionMenu.EditOnDemand')}
                                    </MenuItemOption>
                                </MenuItem>
                            )}
                            {webinar.status === WebinarStatus.Ended && webinar.isOnDemandAvailable && (
                                <MenuItem>
                                    <MenuItemOption to="/$webinarId/on-demand" params={{ webinarId: webinar.webinarId }}>
                                        {t('OptionMenu.WatchOnDemand')}
                                    </MenuItemOption>
                                </MenuItem>
                            )}
                        </Menu>
                        {(webinar.hasBooking || webinar.status !== WebinarStatus.Planned) && (
                            <WebinarButton id={webinar.webinarId} status={webinar.status} title={webinar.title} classNames="flex-1" />
                        )}
                        {!webinar.hasBooking && webinar.status === WebinarStatus.Planned && (
                            <Link to="/$webinarId/edit" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'overview' }} variant="warning" classNames="flex-1">
                                {t('Webinar.Button.Reschedule')}
                                <span className="sr-only">{t('Generic.Link.Context', { title: webinar.title })}</span>
                            </Link>
                        )}
                    </WebinarActions>
                </div>
            </div>
        </article>
    );
};
